import React, { useRef, useState, useEffect, useCallback } from 'react';

const font = `'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
const C = {
  orange: '#FF6B00',
  orangeDim: 'rgba(255,107,0,0.15)',
  orangeBorder: 'rgba(255,107,0,0.4)',
  bg: '#0B1928',
  bgCard: 'rgba(10,20,35,0.92)',
  text: '#F0F4F8',
  muted: '#8496A8',
  green: '#00C851',
  border: 'rgba(255,255,255,0.12)',
};

function formatTime(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export default function ReactionCam({ bracket, onClose, onPicksComplete }) {
  const videoRef       = useRef(null);
  const canvasRef      = useRef(null);
  const mediaRecorder  = useRef(null);
  const chunksRef      = useRef([]);
  const streamRef      = useRef(null);
  const timerRef       = useRef(null);
  const animFrameRef   = useRef(null);

  const [phase, setPhase]           = useState('preview');   // preview | recording | review
  const [picks, setPicks]           = useState({});
  const [currentRound, setCurrentRound] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  const [elapsed, setElapsed]       = useState(0);
  const [camError, setCamError]     = useState(null);
  const [videoBlob, setVideoBlob]   = useState(null);
  const [videoUrl, setVideoUrl]     = useState(null);
  const [showPicker, setShowPicker] = useState(true);
  const [lastPick, setLastPick]     = useState(null);
  const [done, setDone]             = useState(false);

  const rounds = [
    { name: 'Round of 32', matches: 16 },
    { name: 'Round of 16', matches: 8  },
    { name: 'Elite Eight', matches: 4  },
    { name: 'Final Four',  matches: 2  },
    { name: 'Championship',matches: 1  },
  ];

  const getTeams = useCallback((rIdx, mIdx) => {
    if (rIdx === 0) {
      return [bracket?.entries?.[mIdx * 2] || null, bracket?.entries?.[mIdx * 2 + 1] || null];
    }
    return [
      picks[`r${rIdx - 1}_m${mIdx * 2}`] || null,
      picks[`r${rIdx - 1}_m${mIdx * 2 + 1}`] || null,
    ];
  }, [bracket, picks]);

  // Start camera
  useEffect(() => {
    let mounted = true;
    const startCam = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1080 }, height: { ideal: 1920 } },
          audio: true,
        });
        streamRef.current = stream;
        if (videoRef.current && mounted) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      } catch (err) {
        console.error(err);
        if (mounted) setCamError('Camera access denied. Please allow camera & microphone access and try again.');
      }
    };
    startCam();
    return () => {
      mounted = false;
      stopEverything();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopEverything = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
  };

  const startRecording = async () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    setElapsed(0);
    setPhase('recording');

    // Use canvas capture for composited video (cam + overlay)
    const canvas = canvasRef.current;
    const canvasStream = canvas.captureStream(30);
    
    // Add audio from camera stream
    const audioTrack = streamRef.current.getAudioTracks()[0];
    if (audioTrack) canvasStream.addTrack(audioTrack);

    // Start drawing loop
    drawFrame();

    // Pick supported mime type
    const mimeType = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
      .find(t => MediaRecorder.isTypeSupported(t)) || '';

    mediaRecorder.current = new MediaRecorder(canvasStream, mimeType ? { mimeType } : {});
    mediaRecorder.current.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mediaRecorder.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
      const url  = URL.createObjectURL(blob);
      setVideoBlob(blob);
      setVideoUrl(url);
      setPhase('review');
    };
    mediaRecorder.current.start(100);

    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
  };

  const drawFrame = () => {
    const canvas = canvasRef.current;
    const video  = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    // Mirror front camera
    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(video, -W, 0, W, H);
    ctx.restore();

    // Dark gradient overlay at bottom for bracket UI
    const grad = ctx.createLinearGradient(0, H * 0.45, 0, H);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(0.3, 'rgba(6,13,24,0.75)');
    grad.addColorStop(1,   'rgba(6,13,24,0.95)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // BRACKETZ watermark top left
    ctx.fillStyle = '#FF6B00';
    ctx.font = 'bold 36px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('BRACKETZ', 30, 60);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = '18px Arial';
    ctx.fillText('Daily Bracket Challenge', 30, 86);

    animFrameRef.current = requestAnimationFrame(drawFrame);
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    streamRef.current?.getTracks().forEach(t => t.stop());
  };

  const handlePick = (team) => {
    if (!team) return;
    const key = `r${currentRound}_m${currentMatch}`;
    const newPicks = { ...picks, [key]: team };

    // Clear downstream picks
    for (let r = currentRound + 1; r < 5; r++) {
      const nm = Math.floor(currentMatch / Math.pow(2, r - currentRound));
      const nk = `r${r}_m${nm}`;
      const t1 = newPicks[`r${r-1}_m${nm*2}`] || null;
      const t2 = newPicks[`r${r-1}_m${nm*2+1}`] || null;
      if (newPicks[nk] && newPicks[nk] !== t1 && newPicks[nk] !== t2) delete newPicks[nk];
    }

    setPicks(newPicks);
    setLastPick(team);
    setTimeout(() => setLastPick(null), 800);

    // Advance to next match
    const totalMatches = rounds[currentRound].matches;
    if (currentMatch + 1 < totalMatches) {
      setCurrentMatch(m => m + 1);
    } else if (currentRound + 1 < rounds.length) {
      setCurrentRound(r => r + 1);
      setCurrentMatch(0);
    } else {
      setDone(true);
    }
  };

  const handleShare = async () => {
    if (!videoBlob) return;
    const file = new File([videoBlob], 'bracketz-reaction.mp4', { type: videoBlob.type });
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'My Bracketz Reaction',
          text: `I just picked my champion for "${bracket?.category}" on Bracketz! Can you beat me? playbracketz.com #Bracketz`,
          files: [file],
        });
      } catch(e) { console.error(e); }
    } else {
      const a = document.createElement('a');
      a.href = videoUrl;
      a.download = 'bracketz-reaction.mp4';
      a.click();
    }
  };

  const handleFinish = () => {
    if (onPicksComplete && Object.keys(picks).length > 0) onPicksComplete(picks);
    onClose();
  };

  const [t1, t2] = getTeams(currentRound, currentMatch);

  // ── CAMERA ERROR ─────────────────────────────────────────────────────────
  if (camError) {
    return (
      <div style={{ position:'fixed', inset:0, backgroundColor:'#000', zIndex:2000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'32px', fontFamily:font }}>
        <div style={{ fontSize:'48px', marginBottom:'16px' }}>📷</div>
        <div style={{ fontSize:'18px', fontWeight:'700', color:'#F0F4F8', marginBottom:'12px', textAlign:'center' }}>Camera Access Required</div>
        <div style={{ fontSize:'14px', color:'#8496A8', textAlign:'center', lineHeight:'1.6', marginBottom:'28px' }}>{camError}</div>
        <button onClick={onClose} style={{ backgroundColor:C.orange, color:'#fff', border:'none', borderRadius:'12px', padding:'14px 28px', fontSize:'14px', fontWeight:'700', fontFamily:font, cursor:'pointer' }}>Go Back</button>
      </div>
    );
  }

  // ── REVIEW PHASE ──────────────────────────────────────────────────────────
  if (phase === 'review') {
    return (
      <div style={{ position:'fixed', inset:0, backgroundColor:'#000', zIndex:2000, display:'flex', flexDirection:'column', fontFamily:font }}>
        {videoUrl && (
          <video src={videoUrl} controls playsInline style={{ flex:1, objectFit:'contain', backgroundColor:'#000' }}/>
        )}
        <div style={{ padding:'16px 20px 36px', backgroundColor:'#0B1928', borderTop:'1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ fontSize:'16px', fontWeight:'800', color:'#F0F4F8', marginBottom:'4px' }}>Your Reaction is Ready! 🎬</div>
          <div style={{ fontSize:'12px', color:'#8496A8', marginBottom:'16px' }}>Share to TikTok, Reels, or save to your camera roll</div>
          <button onClick={handleShare} style={{ width:'100%', backgroundColor:C.orange, color:'#fff', border:'none', borderRadius:'14px', padding:'16px', fontSize:'15px', fontWeight:'800', fontFamily:font, cursor:'pointer', marginBottom:'10px' }}>
            🚀 Share to TikTok / Reels
          </button>
          <button onClick={handleFinish} style={{ width:'100%', backgroundColor:'transparent', color:'#8496A8', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'14px', padding:'14px', fontSize:'14px', fontWeight:'600', fontFamily:font, cursor:'pointer' }}>
            {Object.keys(picks).length >= 31 ? 'Submit My Bracket' : 'Done'}
          </button>
        </div>
      </div>
    );
  }

  // ── MAIN CAM VIEW ─────────────────────────────────────────────────────────
  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:'#000', zIndex:2000, fontFamily:font, overflow:'hidden' }}>

      {/* Live camera feed */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', transform:'scaleX(-1)' }}
      />

      {/* Hidden canvas for recording */}
      <canvas ref={canvasRef} width={1080} height={1920} style={{ display:'none' }}/>

      {/* Dark gradient at bottom */}
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'55%', background:'linear-gradient(to bottom, transparent, rgba(6,13,24,0.8) 30%, rgba(6,13,24,0.97) 70%)' }}/>

      {/* Top bar */}
      <div style={{ position:'absolute', top:0, left:0, right:0, padding:'52px 20px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:'20px', fontWeight:'900', color:C.orange, letterSpacing:'4px' }}>BRACKETZ</div>
          <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.5)', letterSpacing:'2px' }}>REACTION CAM</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          {phase === 'recording' && (
            <div style={{ display:'flex', alignItems:'center', gap:'6px', backgroundColor:'rgba(0,0,0,0.5)', borderRadius:'20px', padding:'6px 12px' }}>
              <div style={{ width:'8px', height:'8px', borderRadius:'50%', backgroundColor:'#EF4444', animation:'pulse 1s ease infinite' }}/>
              <span style={{ fontSize:'13px', fontWeight:'700', color:'#fff' }}>{formatTime(elapsed)}</span>
            </div>
          )}
          <button onClick={onClose} style={{ backgroundColor:'rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:'50%', width:'36px', height:'36px', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'18px', cursor:'pointer' }}>✕</button>
        </div>
      </div>

      {/* Category label */}
      <div style={{ position:'absolute', top:'50%', left:0, right:0, transform:'translateY(-50%)', textAlign:'center', padding:'0 20px', pointerEvents:'none' }}>
        <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)', letterSpacing:'2px', textTransform:'uppercase', marginBottom:'6px' }}>Today's Bracket</div>
        <div style={{ fontSize:'22px', fontWeight:'800', color:'#fff', textShadow:'0 2px 20px rgba(0,0,0,0.8)', lineHeight:'1.2' }}>{bracket?.category}</div>
      </div>

      {/* Bracket overlay - bottom section */}
      {showPicker && phase !== 'preview' && (
        <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'16px 16px 40px' }}>
          
          {done ? (
            <div style={{ backgroundColor:C.bgCard, borderRadius:'20px', padding:'24px', border:'1px solid rgba(255,215,0,0.4)', textAlign:'center' }}>
              <div style={{ fontSize:'32px', marginBottom:'8px' }}>🏆</div>
              <div style={{ fontSize:'18px', fontWeight:'800', color:'#FFD700', marginBottom:'4px' }}>Champion Picked!</div>
              <div style={{ fontSize:'14px', color:'#F0F4F8', marginBottom:'16px' }}>{picks['r4_m0']}</div>
              <button onClick={stopRecording} style={{ width:'100%', backgroundColor:'#EF4444', color:'#fff', border:'none', borderRadius:'12px', padding:'14px', fontSize:'14px', fontWeight:'800', fontFamily:font, cursor:'pointer' }}>
                ⏹ Stop Recording
              </button>
            </div>
          ) : (
            <div style={{ backgroundColor:C.bgCard, borderRadius:'20px', padding:'16px', border:`1px solid ${C.border}`, backdropFilter:'blur(10px)' }}>
              
              {/* Round label */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                <div style={{ fontSize:'10px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase' }}>{rounds[currentRound].name}</div>
                <div style={{ fontSize:'10px', color:C.muted }}>Match {currentMatch + 1} of {rounds[currentRound].matches}</div>
              </div>

              {/* VS matchup */}
              <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
                <button onClick={() => handlePick(t1)} style={{ flex:1, backgroundColor:lastPick===t1?C.orange:C.orangeDim, border:`1px solid ${lastPick===t1?C.orange:C.orangeBorder}`, borderRadius:'14px', padding:'14px 10px', fontSize:'14px', fontWeight:'700', color:lastPick===t1?'#fff':C.text, fontFamily:font, cursor:'pointer', transition:'all 0.15s', transform:lastPick===t1?'scale(1.03)':'scale(1)' }}>
                  {t1 || 'TBD'}
                </button>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width:'32px', flexShrink:0, fontSize:'11px', fontWeight:'700', color:C.muted }}>VS</div>
                <button onClick={() => handlePick(t2)} style={{ flex:1, backgroundColor:lastPick===t2?C.orange:C.orangeDim, border:`1px solid ${lastPick===t2?C.orange:C.orangeBorder}`, borderRadius:'14px', padding:'14px 10px', fontSize:'14px', fontWeight:'700', color:lastPick===t2?'#fff':C.text, fontFamily:font, cursor:'pointer', transition:'all 0.15s', transform:lastPick===t2?'scale(1.03)':'scale(1)' }}>
                  {t2 || 'TBD'}
                </button>
              </div>

              {/* Progress bar */}
              <div style={{ backgroundColor:'rgba(255,255,255,0.08)', borderRadius:'4px', height:'3px', overflow:'hidden' }}>
                <div style={{ width:`${(Object.keys(picks).length / 31) * 100}%`, height:'100%', backgroundColor:C.orange, borderRadius:'4px', transition:'width 0.3s' }}/>
              </div>
              <div style={{ fontSize:'10px', color:C.muted, textAlign:'center', marginTop:'6px' }}>{Object.keys(picks).length}/31 picks made</div>
            </div>
          )}
        </div>
      )}

      {/* Record / Stop button */}
      <div style={{ position:'absolute', bottom: showPicker && phase !== 'preview' ? '310px' : '50px', left:0, right:0, display:'flex', justifyContent:'center', gap:'16px', alignItems:'center' }}>
        
        {phase === 'preview' && (
          <>
            <button onClick={() => setShowPicker(p => !p)} style={{ backgroundColor:'rgba(0,0,0,0.6)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:'20px', padding:'10px 18px', fontSize:'12px', fontWeight:'700', color:'#fff', fontFamily:font, cursor:'pointer' }}>
              {showPicker ? 'Hide Bracket' : 'Show Bracket'}
            </button>
            <button onClick={startRecording} style={{ width:'72px', height:'72px', borderRadius:'50%', backgroundColor:'#EF4444', border:'4px solid #fff', cursor:'pointer', boxShadow:'0 0 0 4px rgba(239,68,68,0.4)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ width:'28px', height:'28px', borderRadius:'50%', backgroundColor:'#fff' }}/>
            </button>
            <button onClick={() => setShowPicker(p => !p)} style={{ backgroundColor:'rgba(0,0,0,0.6)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:'20px', padding:'10px 18px', fontSize:'12px', fontWeight:'700', color:'#fff', fontFamily:font, cursor:'pointer', opacity:0 }}>
              placeholder
            </button>
          </>
        )}

        {phase === 'recording' && !done && (
          <>
            <button onClick={() => setShowPicker(p => !p)} style={{ backgroundColor:'rgba(0,0,0,0.6)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:'20px', padding:'10px 18px', fontSize:'12px', fontWeight:'700', color:'#fff', fontFamily:font, cursor:'pointer' }}>
              {showPicker ? 'Hide' : 'Show'} Picks
            </button>
            <button onClick={stopRecording} style={{ width:'72px', height:'72px', borderRadius:'50%', backgroundColor:'#EF4444', border:'4px solid #fff', cursor:'pointer', boxShadow:'0 0 0 4px rgba(239,68,68,0.4)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ width:'24px', height:'24px', borderRadius:'4px', backgroundColor:'#fff' }}/>
            </button>
            <div style={{ width:'80px' }}/>
          </>
        )}
      </div>

      {/* Instructions overlay - preview only */}
      {phase === 'preview' && (
        <div style={{ position:'absolute', top:'62%', left:0, right:0, padding:'0 24px', textAlign:'center' }}>
          <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.7)', lineHeight:'1.8' }}>
            <div>🎤 Talk through your picks</div>
            <div>👆 Tap to choose each matchup</div>
            <div>🔴 Hit record when ready</div>
          </div>
        </div>
      )}

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );
}
